import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient, tokenManager } from '../utils/api';

// Query keys
const COMPANY_WHAT_WE_OFFER_QUERY_KEY = 'companyWhatWeOffer';

// Custom hook for fetching company what-we-offer data
export const useCompanyWhatWeOffer = (page = 1, limit = 10) => {
  return useQuery({
    queryKey: [COMPANY_WHAT_WE_OFFER_QUERY_KEY, page, limit],
    queryFn: () => apiClient.get('what-we-offer/company-page', { page, limit }),
    keepPreviousData: true, // Keep previous data while fetching new data
    staleTime: 2 * 60 * 1000, // 2 minutes
    select: (data) => {
      // Transform the API response to match the expected format
      return {
        data: data.data || [],
        total: data.data?.total || 0,
        page: data.data?.page || 1,
        limit: data.data?.limit || 10,
        has_next: data.data?.has_next || false,
        has_prev: data.data?.has_prev || false,
        pagination: {
          current: data.data?.page || 1,
          pageSize: data.data?.limit || 10,
          total: data.data?.total || 0
        }
      };
    }
  });
};

// Custom hook for creating company what-we-offer data
export const useCreateCompanyWhatWeOffer = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data) => apiClient.put('what-we-offer/company-page', data),
    onSuccess: () => {
      // Invalidate and refetch company what-we-offer data
      queryClient.invalidateQueries({ queryKey: [COMPANY_WHAT_WE_OFFER_QUERY_KEY] });
    },
  });
};

// Custom hook for updating company what-we-offer data
export const useUpdateCompanyWhatWeOffer = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }) => apiClient.put(`what-we-offer/company-page/${id}`, data),
    onSuccess: () => {
      // Invalidate and refetch company what-we-offer data
      queryClient.invalidateQueries({ queryKey: [COMPANY_WHAT_WE_OFFER_QUERY_KEY] });
    },
  });
};

// Custom hook for deleting company what-we-offer data
export const useDeleteCompanyWhatWeOffer = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id) => apiClient.delete(`what-we-offer/company-page/${id}`),
    onSuccess: () => {
      // Invalidate and refetch company what-we-offer data
      queryClient.invalidateQueries({ queryKey: [COMPANY_WHAT_WE_OFFER_QUERY_KEY] });
    },
  });
};

// Custom hook for getting a single company what-we-offer item
export const useCompanyWhatWeOfferItem = (id) => {
  return useQuery({
    queryKey: [COMPANY_WHAT_WE_OFFER_QUERY_KEY, id],
    queryFn: () => apiClient.get(`what-we-offer/company-page/${id}`),
    enabled: !!id, // Only run query if id exists
  });
}; 